import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { getOrgId } from '../services/orgStore';
import * as quickBooksStatusService from '../services/quickBooksStatusService';

const QuickBooksStatusContext = createContext(null);

/**
 * Holds QuickBooks "connected for this user + org" and refetches when
 * `navigationEpoch` changes (e.g. sidebar view) so status stays fresh after OAuth or disconnect.
 */
export function QuickBooksStatusProvider({ navigationEpoch, children }) {
  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [error, setError] = useState('');

  const refresh = useCallback(async () => {
    const orgId = getOrgId();
    if (!orgId) {
      setLoading(false);
      setConnected(false);
      setUpdatedAt(null);
      setError('');
      return;
    }
    setLoading(true);
    try {
      const data = await quickBooksStatusService.fetchQuickBooksStatus(orgId);
      setConnected(!!data.connected);
      setUpdatedAt(data.updated_at);
      setError(data.error || '');
    } catch (e) {
      setConnected(false);
      setUpdatedAt(null);
      setError(e?.message || 'Failed to load QuickBooks status.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh, navigationEpoch]);

  const value = useMemo(
    () => ({
      loading,
      connected,
      updatedAt,
      error,
      refresh,
    }),
    [loading, connected, updatedAt, error, refresh]
  );

  return (
    <QuickBooksStatusContext.Provider value={value}>
      {children}
    </QuickBooksStatusContext.Provider>
  );
}

export function useQuickBooksStatus() {
  const ctx = useContext(QuickBooksStatusContext);
  if (!ctx) {
    throw new Error('useQuickBooksStatus must be used within QuickBooksStatusProvider');
  }
  return ctx;
}
